import {
  Button,
  FieldLabel,
  Flex,
  FormGroup,
  FormGroupLegend,
  Icon,
  Label,
} from "@braze/beacon-core";
import { RoundedCard } from "../../shared/RoundedCard";
import styled from "styled-components";
import { Select } from "@braze/beacon-lab-select";
import {
  BUDGET_SOURCE,
  QUARTERS,
  YEARS,
  CAMPAIGN_REGION,
  CAMPAIGN_OBJECTIVE,
  CAMPAIGN_SOURCE,
  CAMPAIGN_SUBREGIONS,
  REQUIRED_FIELDS,
  INDUSTRY,
  AUDIENCE_TYPE,
} from "../../constants/SelectConstants";
import { ViewTitle } from "../../shared/ViewTitle";
import { useEffect, useState } from "react";
import { getColor } from "@braze/beacon-styling";
import { CampaignObject, MarketoProgramObject, ParentCampaignObject } from "../../types/APITypes";
import { OptionsList, formatOption } from "../../util/optionsFormatter";
import { usePostFormSubmission } from "../../hooks/usePostFormSubmission";
import { useFormContext } from "../../contexts/FormContext";
import { useGlobalAppContext } from "../../contexts/GlobalAppContext";
import { RequiredSelect } from "../../shared/forms/RequiredSelect";
import { RequiredDate } from "../../shared/forms/RequiredDate";
import { RequiredText } from "../../shared/forms/RequiredText";
import { OptionalCheckbox } from "../../shared/forms/OptionalCheckbox";
import { RequiredNumber } from "../../shared/forms/RequiredNumber";
import { FieldProps } from "../../shared/forms/types";
import { RequiredTextArea } from "../../shared/forms/RequiredTextArea";
import { FormContainer } from "../../shared/forms/FormContainer";
import { OptionalSelect } from "../../shared/forms/OptionalSelect";
import { REACT_APP_NODE_ENV } from "../../constants/env";

// Sample data for auto-filling the form
const sampleCampaignData: Partial<CampaignObject> = {
  campaign_region: "AMER",
  campaign_subregion: "AMER East",
  campaign_name: "Auto-filled Campaign",
  start_date: new Date().toISOString().split('T')[0],
  end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  fiscal_year: 25,
  fiscal_quarter: 1,
  campaign_description: "This is a test campaign description for development purposes",
  campaign_owner: "Test Owner",
  campaign_owner_email: "test@braze.com",
  campaign_type: "Webinar",
  parent_campaign: "", // Will be set dynamically based on available parent campaigns
  campaign_objective: "Lead Generation",
  campaign_source: "External",
  audience_type: "Prospect",
  industry: "Technology",
  abm: false,
  offerfit: false,
  campaign_partner: false,
  budget_source: "Marketing",
  budget_amount: 10000,
  nnl_projected_count: 100,
  mql_projected_count: 50,
  sql_projected_count: 25,
  pipe_count_projected: 10,
  pipe_amount_projected: 100000,
};

export const CampaignForm = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { campaign, setCampaign } = useFormContext();
  const [submitForm] = usePostFormSubmission();
  const { parentCampaigns, programs, setRefresh } = useGlobalAppContext();
  const handleFieldChange = (key: keyof CampaignObject) => {
    return (value: string | number) => {
      let newVal;
      if (typeof value === "object" && value != null) {
        if (key === "parent_campaign") {
          let selectedParent = parentCampaigns?.filter((p) => p["parent_campaign_name"] === value["value"]);
          newVal = {
            [key]: value["value"],
            parent_campaign_id: selectedParent[0]["parent_campaign_id"],
            marketing_program_id: selectedParent[0]["marketing_program_id"],
          };
        } else {
          newVal = { [key]: value["value"] };
        }
      } else {
        newVal = { [key]: value };
      }
      setCampaign({ ...campaign, ...newVal });
    };
  };

  const autoFillForm = () => {
    // Clone the sample data to avoid modifying the original
    const formData = { ...sampleCampaignData };

    // Set parent campaign dynamically if available
    if (parentCampaigns && parentCampaigns.length > 0) {
      const firstParent = parentCampaigns[0];
      formData.parent_campaign = firstParent.parent_campaign_name;
      formData.parent_campaign_id = firstParent.parent_campaign_id;
      formData.marketing_program_id = firstParent.marketing_program_id;
    }

    // Update the form with sample data
    setCampaign({ ...campaign, ...formData });
  };

  const [submitButton, setSubmitButton] = useState(true);
  useEffect(() => {
    let canSubmit = REQUIRED_FIELDS.every((e) => {
      return campaign[e] != null && campaign[e] !== "";
    });
    setSubmitButton(!canSubmit);
  }, [campaign]);

  // Determine if we're in development mode
  const isDevMode = REACT_APP_NODE_ENV === "development";

  return (
    <>
      {!submitted && (
        <RoundedCard style={{ minWidth: "70%", backgroundColor: "rgba(256, 256, 256, 0.5)" }}>
          <Flex style={{ justifyContent: "space-between", alignItems: "center" }}>
            <ViewTitle title="Create a New Campaign" />
            {isDevMode && (
              <Flex direction="column" style={{ width: "200px" }}>
                <Button
                  onClick={autoFillForm}
                  variant="secondary"
                >
                  [Test] Auto-Fill Form
                </Button>
                <p style={{ fontSize: "12px", color: "gray", marginTop: "4px" }}>
                  Form values won't show in the UI but the state will be updated such that you can submit the form
                </p>
              </Flex>
            )}
          </Flex>
          <FormGroup style={{ width: "100%", marginBottom: "1rem" }}>
            <FormGroupLegend>Fill out the intake form to submit a new campaign for approval</FormGroupLegend>
            <GroupCard title="Region Fields">
              <HorizontalGroupContainer>
                <RequiredSelect
                  name="Campaign Region"
                  options={formatOption(CAMPAIGN_REGION)}
                  handleChange={handleFieldChange("campaign_region")}
                />
                <SubRegionSelect
                  name="Campaign Sub Region"
                  options={formatOption(CAMPAIGN_SUBREGIONS, true)}
                  handleChange={handleFieldChange("campaign_subregion")}
                />
              </HorizontalGroupContainer>
            </GroupCard>
            <GroupCard title="Campaign Details">
              <RequiredText
                name="Campaign Name"
                handleChange={handleFieldChange("campaign_name")}
                maxLength={80}
                info="Name of the Event, Content, Activity, etc. - NOT FY-Q... formatted title"
              />
              <HorizontalGroupContainer>
                <RequiredDate name="Start Date" handleChange={handleFieldChange("start_date")} />
                <RequiredDate
                  name="End Date"
                  handleChange={handleFieldChange("end_date")}
                  minDate={campaign.start_date}
                />
              </HorizontalGroupContainer>
              <HorizontalGroupContainer>
                <RequiredSelect
                  name="Fiscal Year"
                  options={formatOption(YEARS)}
                  handleChange={handleFieldChange("fiscal_year")}
                />
                <RequiredSelect
                  name="Fiscal Quarter"
                  options={formatOption(QUARTERS)}
                  handleChange={handleFieldChange("fiscal_quarter")}
                />
              </HorizontalGroupContainer>
              <RequiredTextArea
                name="Campaign Description"
                handleChange={handleFieldChange("campaign_description")}
                info="Describe your campaign/event/content/activity such as including the event/content page URL. This is to provide reference details to marketing and sales during the campaign leads followup"
              />
            </GroupCard>

            <GroupCard title="Owner Details">
              <HorizontalGroupContainer>
                <RequiredText name="Campaign Owner" handleChange={handleFieldChange("campaign_owner")} />
                <RequiredText
                  name="Campaign Owner Email"
                  handleChange={handleFieldChange("campaign_owner_email")}
                  info="Email must be the email assigned in Salesforce - not an alias"
                />
              </HorizontalGroupContainer>
            </GroupCard>

            <GroupCard title="Campaign Fields">
              <HorizontalGroupContainer>
                <RequiredSelect
                  name="Campaign Type"
                  options={formatOption(
                    programs.map((p: MarketoProgramObject) => p["sfdc_type"]),
                    false
                  )}
                  handleChange={handleFieldChange("campaign_type")}
                />
                <RequiredSelect
                  name="Parent Campaign"
                  options={formatOption(
                  parentCampaigns
                    .filter((p: ParentCampaignObject) => p.is_active)
                    .map((p: ParentCampaignObject) => p["parent_campaign_name"]),
                  false
                  )}
                  handleChange={handleFieldChange("parent_campaign")}
                />
              </HorizontalGroupContainer>

              <HorizontalGroupContainer>
                <RequiredSelect
                  name="Campaign Objective"
                  options={formatOption(CAMPAIGN_OBJECTIVE)}
                  handleChange={handleFieldChange("campaign_objective")}
                />
                <RequiredSelect
                  name="Campaign Source"
                  options={formatOption(CAMPAIGN_SOURCE)}
                  handleChange={handleFieldChange("campaign_source")}
                />
              </HorizontalGroupContainer>

              <HorizontalGroupContainer>
                <RequiredSelect
                  name="Audience Type"
                  options={formatOption(AUDIENCE_TYPE)}
                  handleChange={handleFieldChange("audience_type")}
                />
                <OptionalSelect
                  name="Industry"
                  options={formatOption(INDUSTRY)}
                  handleChange={handleFieldChange("industry")}
                />
                <OptionalCheckbox name="ABM" handleChange={handleFieldChange("abm")} />
                <OptionalCheckbox name="OfferFit" handleChange={handleFieldChange("offerfit")} />
              </HorizontalGroupContainer>
            </GroupCard>

            <GroupCard title="Partner Fields">
              <OptionalCheckbox name="Campaign Partner" text="Click the checkbox if the campaign involves a partner, otherwise leave empty." handleChange={handleFieldChange("campaign_partner")} />
            </GroupCard>

            <GroupCard title="Budget Details">
              <HorizontalGroupContainer>
                <RequiredSelect
                  name="Budget Source"
                  options={formatOption(BUDGET_SOURCE)}
                  handleChange={handleFieldChange("budget_source")}
                />
                <RequiredNumber name="Budget ($)" handleChange={handleFieldChange("budget_amount")} />
              </HorizontalGroupContainer>
            </GroupCard>

            <GroupCard title="Projected Lead Estimates">
              <HorizontalGroupContainer>
                <RequiredNumber name="Projected NNLs" handleChange={handleFieldChange("nnl_projected_count")} />
                <RequiredNumber name="Projected MQLs" handleChange={handleFieldChange("mql_projected_count")} />
                <RequiredNumber name="Projected SQLs" handleChange={handleFieldChange("sql_projected_count")} />
              </HorizontalGroupContainer>
            </GroupCard>

            <GroupCard title="Projected Pipe Estimates">
              <HorizontalGroupContainer>
                <RequiredNumber name="Projected Pipe Count" handleChange={handleFieldChange("pipe_count_projected")} />
                <RequiredNumber
                  name="Projected Pipe Amount ($)"
                  handleChange={handleFieldChange("pipe_amount_projected")}
                />
              </HorizontalGroupContainer>
            </GroupCard>
          </FormGroup>
          <Button
            onClick={() => {
              setLoading(true);
              submitForm(campaign);
              setSubmitted(true);
              setRefresh(true);
            }}
            disabled={submitButton}
            loadingState={!loading ? "idle" : "loading"}
          >
            Submit Campaign Details
          </Button>
        </RoundedCard>
      )}
      {submitted && <SubmittedRedirect />}
    </>
  );
};

const SubmittedRedirect = () => {
  return (
    <RoundedCard>
      <SubmittedBox>
        <Icon className="check" name="circle-check" size="5x" />
        <Label className="message">
          Your campaign details have been successfully submitted and are now waiting for approval.
        </Label>
      </SubmittedBox>
    </RoundedCard>
  );
};

const SubmittedBox = styled(Flex)`
  flex-direction: column;
  align-items: center;

  .check {
    color: ${getColor("lightGreen", 500)};
    margin-bottom: 4rem;
    margin-top: 2rem;
  }
  .message {
    font-size: 20px;
    margin-bottom: 3rem;
  }
`;

const GroupCard = ({ title, children }: { title: string; children: React.ReactNode }) => {
  const GroupCardStyle = {
    marginBottom: "2rem",
  };

  return (
    <>
      <Label style={{ fontSize: "20px" }}>{title}</Label>
      <GroupContainer style={GroupCardStyle}>{children}</GroupContainer>
    </>
  );
};

const HorizontalGroupContainer = styled(Flex)`
  > * {
    &:not(:last-of-type) {
      margin-right: 20px;
    }
  }
`;

const GroupContainer = styled(Flex)`
  background: #fff;
  border-radius: 1.5em;
  box-shadow: 0px 12px 24px rgba(69, 84, 94, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.05);
  padding: 1rem 2rem;
  flex-direction: column;
`;
interface SubRegionProps extends FieldProps {
  options: OptionsList[];
  info?: string;
}
const SubRegionSelect = (props: SubRegionProps) => {
  const [invalid, setInvalid] = useState(true);
  const { campaign } = useFormContext();

  return (
    <FormContainer>
      <FieldLabel label={props.name} status={invalid ? "danger" : "success"} statusText={props.name + " is required"}>
        <Select
          fluid
          options={props.options}
          invalid={invalid}
          isDisabled={campaign.campaign_region == null}
          filterOption={(o): boolean => {
            return o.value.slice(0, 4) === campaign.campaign_region || o.value === "None";
          }}
          onChange={(selectedOption) => {
            if (!selectedOption) {
              setInvalid(true);
              props.handleChange(selectedOption);
            } else {
              setInvalid(false);
              props.handleChange(selectedOption);
            }
          }}
        />
      </FieldLabel>
    </FormContainer>
  );
};
